import React, { useState } from 'react'
import {
  Box,
  Button,
  Collapse,
  Group,
  Loader,
  Paper,
  Select,
  SelectProps,
  Stack,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core'
import { IconArrowBackUp, IconCheck } from '@tabler/icons-react'
import IonIcon from '@reacticons/ionicons'
import PrimaryInput from 'app/views/components/buttons/PrimaryInput'
import { TradeNoteItem } from 'app/state/ducks/trade-notes/types'
import {
  ALL_SUB_TRADES,
  ActivitySourceFilter,
  ActivityStreamViewModel,
  MAX_NOTE_LENGTH,
  actionedLine,
  formatActivityTimestamp,
  isNewOnLendos,
  reopenedLine,
} from './activity-stream-form-logic'

/**
 * Tokens from the Activity Stream prototype planning spec
 * (`activity-stream/Activity stream prototype planning/uploads/Activity Stream.md`): --primary
 * blue, --secondary purple, --accent dark purple, --foreground / --muted-foreground text,
 * --border. The spec's rules that shape this file: note rows are a purple TINT with no border
 * ("bg-secondary/12"); badges are SOLID backgrounds, never low-opacity washes; a left stripe
 * belongs to EVENT rows only, which are Phase 2. The unread treatment (pink tint + stripe +
 * New badge) shipped with read state — see activity-stream/read-state-design.md.
 */
const tokens = {
  blue: '#32439A',
  ink: '#45526C',
  muted: '#999EA3',
  line: '#B1B1B1',
  /* Control borders are one step lighter than --border, per the prototype (--neutral-300). */
  controlBorder: '#D4D5D9',
  /* Card borders and the timeline connector are lighter still (--neutral-200). */
  cardBorder: '#E6E7EB',
  controlBg: '#F7F7F7',
  pink: '#E8006E',
  notePurple: '#7E3AF2',
  /* Timestamp meta ink — the prototype's --purple-700, derived from --secondary #7E3AF2. */
  purple700: '#6D28D9',
  /* Unread row treatment (read-state design): --pink-100 tint + pink stripe + NEW badge. */
  unreadTint: '#FDE5EE',
  /* The "Actioned by" check — the success green the app's status pills already use. */
  green: '#287A26',
  cardShadow: '0 2px 6px rgba(0, 21, 101, 0.08)',
}

/** The composer's row labels (Quick Note, Sub-Trade), matching the Signing Portal's. */
const composerLabelSx = { fontSize: 13, fontWeight: 400, color: '#001565', whiteSpace: 'nowrap' } as const

/**
 * The composer's selects, styled as the Signing Portal's (LOS-6990): a 1px outline drawn on the
 * wrapper, and the app's single chevron (IonIcon in .customRightSection, which lets clicks
 * through to the input) in place of Mantine's double chevron.
 *
 * Placeholder and options are 13px, as in the prototype, overriding general.css (a 12px
 * placeholder font and 14px !important items). Options never wrap: the dropdown grows to its
 * longest option instead, but never narrower than the select, as a native select list does.
 *
 * With the outline on the wrapper, the input's own focus ring is gone, so the wrapper carries
 * the prototype's focus state instead (--lendos-blue border + --shadow-focus).
 */
const ComposerSelect = ({ width, ...props }: SelectProps & { width: number }) => (
  <Select
    withinPortal
    rightSection={<IonIcon name="chevron-down-outline" />}
    classNames={{ rightSection: 'customRightSection' }}
    sx={{ width, flexShrink: 0 }}
    styles={{
      wrapper: {
        border: `1px solid ${tokens.controlBorder}`,
        borderRadius: 4,
        overflow: 'hidden',
        '&:focus-within': {
          borderColor: '#001565',
          boxShadow: '0 0 0 3px rgba(0, 21, 101, 0.20)',
        },
      },
      input: {
        height: 30,
        minHeight: 30,
        border: 'none',
        fontSize: 13,
        color: tokens.ink,
        '&::placeholder': { font: 'inherit' },
      },
      dropdown: { width: 'max-content !important', minWidth: width },
      item: { fontSize: '13px !important', whiteSpace: 'nowrap' },
    }}
    {...props}
  />
)

const SOURCE_FILTERS: { value: ActivitySourceFilter; label: string }[] = [
  { value: 'All', label: 'All' },
  { value: 'LendOS', label: 'LendOS' },
  // Public/product name for the portal is 'Connect' (matches the CONNECT badge); the VALUE
  // stays 'SigningPortal' — it is the API's source discriminator, not a display string.
  { value: 'SigningPortal', label: 'Connect' },
]

/** A pill chip in the Filters panel — the prototype's chip shape. */
const FilterChip: React.FC<{ label: string; active: boolean; onClick: () => void }> = ({
  label,
  active,
  onClick,
}) => (
  <Box
    component="button"
    onClick={onClick}
    sx={{
      padding: '5px 10px',
      borderRadius: 999,
      fontSize: 11,
      fontWeight: 600,
      cursor: 'pointer',
      border: `1px solid ${active ? tokens.blue : tokens.controlBorder}`,
      background: active ? tokens.blue : '#FFFFFF',
      color: active ? '#FFFFFF' : tokens.muted,
    }}
  >
    {label}
  </Box>
)

/**
 * One note on the timeline, matching the prototype's tab-page row exactly: white row with a 4px
 * purple left stripe and the dot-and-connector spine; one header line — "Note" · By author ·
 * "· {sub-trade id}" · source badge · right-aligned purple timestamp — body below. The tab's
 * type scale is deliberately smaller than the drawer's (14px title / 186px timestamp column vs
 * 16px / 200px).
 *
 * The sub-trade chip exists only on this tab: it aggregates notes across the master trade's
 * sub-trades, so a note needs to say which one it belongs to (the drawer is already scoped to
 * one). The label is the display system id, resolved from the sub-trade options — a note whose
 * sub-trade cannot be resolved shows no chip rather than a raw StableKey.
 *
 * The source badge marks only CROSS-SURFACE notes: on this tab, a note written in the Signing
 * Portal, labeled "Connect" per the LendOS brief, on the prototype's blue.
 */
export const NoteRow: React.FC<{
  item: TradeNoteItem
  isLast: boolean
  subTradeLabel?: string
  onMarkRead: (item: TradeNoteItem) => Promise<boolean>
  /** Undo "Mark as actioned" (LOS-6988). Absent, the row offers no Undo. */
  onReopen?: (item: TradeNoteItem) => Promise<boolean>
  /** A "Mark all" is in flight — this row's own button waits for it (LOS-6989). */
  disabled?: boolean
}> = ({ item, isLast, subTradeLabel, onMarkRead, onReopen, disabled = false }) => {
  // Unread = portal-authored + no receipt. Only the "Mark as actioned" button clears it
  // (LOS-6987) — nothing else on the card does, so a stray click cannot acknowledge a note.
  const isNew = isNewOnLendos(item)
  const actioned = actionedLine(item)
  const reopened = reopenedLine(item)
  const [isMarking, setIsMarking] = useState(false)
  const [isReopening, setIsReopening] = useState(false)

  const markActioned = async () => {
    // Mantine's loading state blocks the pointer only (CSS); this also stops a keyboard repeat.
    if (isMarking || disabled) return
    setIsMarking(true)
    // On success the upserted item re-renders this row without the button; on failure the
    // button comes back for a retry.
    await onMarkRead(item)
    setIsMarking(false)
  }

  const undo = async () => {
    if (!onReopen || isReopening || disabled) return
    setIsReopening(true)
    // On success the upserted item re-renders this row unactioned, with "Reopened by".
    await onReopen(item)
    setIsReopening(false)
  }

  return (
    <Box
      mt={4}
      sx={{
        display: 'flex',
        gap: 14,
        padding: '14px 12px 14px 10px',
        borderRadius: 6,
        background: isNew ? tokens.unreadTint : '#FFFFFF',
        borderLeft: `4px solid ${isNew ? tokens.pink : tokens.notePurple}`,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 6,
          flexShrink: 0,
          paddingTop: 4,
        }}
      >
        <Box
          sx={{
            width: 12,
            height: 12,
            flexShrink: 0,
            borderRadius: '50%',
            background: tokens.notePurple,
          }}
        />
        {!isLast && <Box sx={{ width: 2, flex: '1 0 0', background: tokens.cardBorder }} />}
      </Box>

      <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
        <Group spacing={8} mb={4} noWrap>
          <Text sx={{ fontSize: 14, fontWeight: 600, color: tokens.blue, whiteSpace: 'nowrap' }}>
            Note
          </Text>
          <Text
            truncate
            sx={{ fontSize: 13, fontWeight: 600, color: tokens.ink, minWidth: 0 }}
          >
            By {item.authorName}
          </Text>
          {subTradeLabel && (
            <Text sx={{ fontSize: 12, fontWeight: 600, color: tokens.blue, whiteSpace: 'nowrap' }}>
              · {subTradeLabel}
            </Text>
          )}
          {isNew && (
            <Box
              sx={{
                padding: '3px 9px',
                borderRadius: 3,
                fontSize: 11,
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                whiteSpace: 'nowrap',
                color: '#FFFFFF',
                background: tokens.pink,
              }}
            >
              New
            </Box>
          )}
          {item.source === 'SigningPortal' && (
            <Box
              sx={{
                padding: '2px 7px',
                borderRadius: 3,
                fontSize: 10,
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.07em',
                whiteSpace: 'nowrap',
                color: '#FFFFFF',
                background: tokens.blue,
              }}
            >
              Connect
            </Box>
          )}
          {/* The prototype right-aligns the timestamp in a fixed column so rows scan evenly.
            Viewer-local clock (the app-wide convention); the title carries the absolute ISO
            instant for cross-zone disambiguation. */}
          <Text
            title={item.createdAt}
            sx={{
              marginLeft: 'auto',
              flexShrink: 0,
              width: 186,
              whiteSpace: 'nowrap',
              textAlign: 'right',
              fontVariantNumeric: 'tabular-nums',
              fontSize: 12,
              color: tokens.purple700,
            }}
          >
            {formatActivityTimestamp(item.createdAt)}
          </Text>
        </Group>

        {/* Free text typed by users — render as plain text, never as markup. */}
        <Text
          sx={{
            fontSize: 14,
            lineHeight: 1.5,
            color: tokens.ink,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {item.body}
        </Text>

        {/* Footer (LOS-6987, LOS-6988): only the MOST RECENT event shows — "Reopened by" above
          the button while an undone note is unactioned again, the attribution line (with its
          quiet Undo) once it is actioned. */}
        {reopened && (
          <Group spacing={6} mt={10} noWrap>
            <IconArrowBackUp size={13} stroke={2.2} color={tokens.muted} style={{ flexShrink: 0 }} />
            <Text sx={{ fontSize: 11, color: tokens.ink }}>{reopened}</Text>
          </Group>
        )}
        {isNew && (
          <Button
            mt={10}
            variant="default"
            loading={isMarking}
            disabled={disabled}
            loaderProps={{ color: tokens.blue }}
            onClick={markActioned}
            sx={{
              height: 'auto',
              padding: '5px 12px',
              border: `1px solid ${tokens.blue}`,
              borderRadius: 5,
              background: '#FFFFFF',
              color: tokens.blue,
              fontSize: 12,
              fontWeight: 600,
              transition: 'all 120ms cubic-bezier(0.2, 0, 0, 1)',
              '&:hover': { background: tokens.blue, color: '#FFFFFF' },
            }}
          >
            Mark as actioned
          </Button>
        )}
        {actioned && (
          <Group spacing={6} mt={10} noWrap>
            <IconCheck size={13} stroke={2.6} color={tokens.green} style={{ flexShrink: 0 }} />
            <Text sx={{ fontSize: 11, color: tokens.ink }}>{actioned}</Text>
            {onReopen && (
              <Box
                component="button"
                type="button"
                onClick={undo}
                disabled={isReopening || disabled}
                sx={{
                  border: 'none',
                  background: 'none',
                  padding: 0,
                  marginLeft: 2,
                  color: tokens.pink,
                  fontSize: 11,
                  fontWeight: 600,
                  textDecoration: 'underline',
                  cursor: isReopening || disabled ? 'default' : 'pointer',
                  '&:hover:not(:disabled)': { color: tokens.blue },
                  '&:disabled': { opacity: 0.6 },
                }}
              >
                {isReopening ? 'Undoing…' : 'Undo'}
              </Box>
            )}
          </Group>
        )}
      </Box>
    </Box>
  )
}

const AddNoteComposer: React.FC<{ stream: ActivityStreamViewModel }> = ({ stream }) => {
  const needsSubTradeChoice = stream.subTradeOptions.length > 1
  const hasNoSubTrades = stream.subTradeOptions.length === 0

  return (
    <Paper
      sx={{
        border: `1px solid ${tokens.controlBorder}`,
        borderRadius: 10,
        background: '#FFFFFF',
        boxShadow: tokens.cardShadow,
        padding: '14px 16px',
      }}
    >
      {/* The prototype's compact tab composer: title, the Quick Note row, then ONE row —
          "Sub-Trade" label, the select (with an "All sub-trades (N)" option), and the
          character count right — then the textarea and Save/Cancel bottom-left. */}
      <Text mb={10} sx={{ fontSize: 15, fontWeight: 600, color: tokens.blue }}>
        Create New Note
      </Text>

      {/* LOS-6990: a pick appends its text to the note and never sends it. The select holds
          no value, so it shows its placeholder again straight away, ready for another pick. */}
      <Group mb={10} spacing={10} noWrap>
        <Text sx={composerLabelSx}>Quick Note</Text>
        <ComposerSelect
          width={320}
          placeholder="Select a quick note…"
          aria-label="Quick Note"
          data={stream.quickNoteOptions}
          value={null}
          onChange={stream.addQuickNote}
          disabled={hasNoSubTrades}
        />
      </Group>

      <Group mb={10} spacing={10} noWrap>
        {needsSubTradeChoice && (
          <>
            <Text sx={composerLabelSx}>Sub-Trade</Text>
            <ComposerSelect
              width={240}
              placeholder="Select a sub-trade"
              data={[
                ...stream.subTradeOptions,
                {
                  value: ALL_SUB_TRADES,
                  label: `All sub-trades (${stream.subTradeOptions.length})`,
                },
              ]}
              value={stream.selectedSubTradeId}
              onChange={stream.setSelectedSubTradeId}
            />
          </>
        )}
        <Text
          sx={{
            flex: '1 1 auto',
            textAlign: 'right',
            fontSize: 11,
            color: tokens.muted,
            whiteSpace: 'nowrap',
          }}
        >
          {stream.body.length} / {MAX_NOTE_LENGTH} characters
        </Text>
      </Group>

      <Textarea
        placeholder={`Enter your note (maximum ${MAX_NOTE_LENGTH} character limit)…`}
        minRows={3}
        autosize
        maxLength={MAX_NOTE_LENGTH}
        value={stream.body}
        onChange={event => stream.setBody(event.currentTarget.value)}
        disabled={hasNoSubTrades}
      />
      {hasNoSubTrades && (
        <Text mt={6} sx={{ fontSize: 12, color: tokens.muted }}>
          This trade has no sub-trades yet, so a note cannot be added.
        </Text>
      )}

      <Group mt={14} spacing={10}>
        <PrimaryInput
          text={stream.isSubmitting ? 'Saving…' : 'Save Note'}
          onClick={stream.submit}
          disabled={!stream.canSubmit}
          loading={stream.isSubmitting}
          w="auto"
          h="36px"
          px={22}
          sx={{
            /* Disabled state per the prototype: solid --neutral-400, white label. */
            '&:disabled, &[data-disabled]': {
              background: '#B9BAC0',
              color: '#FFFFFF',
              border: 'none',
              cursor: 'not-allowed',
            },
          }}
        />
        <Button
          variant="default"
          onClick={stream.cancelComposer}
          sx={{
            height: 36,
            borderRadius: 5,
            padding: '0 22px',
            borderColor: tokens.controlBorder,
            color: tokens.ink,
            fontSize: 13,
            fontWeight: 500,
          }}
        >
          Cancel
        </Button>
      </Group>
    </Paper>
  )
}

/**
 * Presentation for the trade Activity Stream tab (ADR_LOS-028), built to the design prototype
 * (`activity-stream/Activity Stream.dc.html`) in its notes-only mode, per the design brief
 * (`activity-stream/LendOS Activity Stream page (Notes Phase 1).md`).
 *
 * Read state shipped (activity-stream/read-state-design.md): the tab badge and the unread
 * row treatment + "New" badge are live — an unread row is a SigningPortal-origin note with no
 * receipt, and clicking it marks it read (first-click-wins). Still absent on purpose: replies
 * (the brief leaves that toggle "pending decision, leaning to new note only"; the API cannot
 * persist `parentNoteId` anyway). Event rows and the category legend belong to the prototype's
 * all-items mode; Phase 1 emits only notes (D9) — the item switch below is the seam events land
 * in. The Filters panel ships its one Phase-1-answerable group: the source system.
 */
export const ActivityStreamPresentation: React.FC<{ stream: ActivityStreamViewModel }> = ({
  stream,
}) => {
  const { items } = stream
  const activeFilterCount = stream.sourceFilter === 'All' ? 0 : 1

  return (
    <Stack w="100%" spacing={16}>
      <Collapse in={stream.isComposerOpen}>
        <AddNoteComposer stream={stream} />
      </Collapse>

      <Paper
        sx={{
          border: `1px solid ${tokens.cardBorder}`,
          borderRadius: 10,
          background: '#FFFFFF',
          boxShadow: tokens.cardShadow,
          padding: '18px 20px 20px',
        }}
      >
        {/* The prototype's single controls row: search · sort · Filters · + Add Note. Refresh is
            not in the design, but Phase 1 surfaces new notes by refetching and nothing else
            (ADR_LOS-028 D8) — without it the stream can only be reloaded by leaving the tab. */}
        <Group spacing={10} noWrap>
          <TextInput
            placeholder="Search notes…"
            value={stream.search}
            onChange={event => stream.setSearch(event.currentTarget.value)}
            sx={{ flex: '1 1 auto', minWidth: 0 }}
            styles={{
              input: {
                height: 32,
                borderRadius: 5,
                borderColor: tokens.controlBorder,
                fontSize: 13,
                color: tokens.ink,
              },
            }}
          />
          <Text sx={{ fontSize: 13, color: tokens.muted, whiteSpace: 'nowrap' }}>Sort:</Text>
          <Button
            variant="default"
            onClick={stream.toggleSortOrder}
            sx={{
              height: 32,
              borderRadius: 5,
              padding: '0 12px',
              borderColor: tokens.controlBorder,
              color: tokens.ink,
              fontSize: 13,
              fontWeight: 400,
              flexShrink: 0,
            }}
          >
            {stream.sortOrder === 'newest' ? 'Newest First' : 'Oldest First'}
          </Button>
          <Button
            variant={stream.filtersOpen ? 'filled' : 'default'}
            onClick={stream.toggleFilters}
            sx={{
              height: 32,
              borderRadius: 5,
              padding: '0 13px',
              fontSize: 13,
              fontWeight: 500,
              flexShrink: 0,
              ...(stream.filtersOpen
                ? { background: tokens.blue, '&:hover': { background: tokens.blue } }
                : { borderColor: tokens.controlBorder, color: tokens.blue }),
            }}
          >
            Filters{activeFilterCount ? ` Active · ${activeFilterCount}` : ''}
          </Button>
          <Button
            variant="default"
            leftIcon={
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.8}
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ width: 15, height: 15 }}
              >
                <path d="M21 12a9 9 0 1 1-3-6.7M21 4v5h-5" />
              </svg>
            }
            onClick={stream.refresh}
            loading={stream.isLoading}
            sx={{
              height: 32,
              borderRadius: 5,
              padding: '0 13px',
              borderColor: tokens.controlBorder,
              color: tokens.blue,
              fontSize: 13,
              fontWeight: 500,
              flexShrink: 0,
            }}
          >
            Refresh
          </Button>
          <PrimaryInput
            text="+ Add Note"
            onClick={stream.openComposer}
            w="auto"
            h="32px"
            px={15}
          />
        </Group>

        <Collapse in={stream.filtersOpen}>
          <Box
            mt={12}
            p={14}
            sx={{
              background: tokens.controlBg,
              border: `1px solid ${tokens.line}`,
              borderRadius: 5,
            }}
          >
            <Group position="apart" align="flex-end">
              <Box>
                <Text
                  mb={6}
                  sx={{
                    fontSize: 10,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.09em',
                    color: tokens.muted,
                  }}
                >
                  Source System
                </Text>
                <Group spacing={6}>
                  {SOURCE_FILTERS.map(option => (
                    <FilterChip
                      key={option.value}
                      label={option.label}
                      active={stream.sourceFilter === option.value}
                      onClick={() => stream.setSourceFilter(option.value)}
                    />
                  ))}
                </Group>
              </Box>
              <Box
                component="button"
                onClick={stream.clearFilters}
                sx={{
                  border: 'none',
                  background: 'none',
                  color: tokens.pink,
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Clear All Filters
              </Box>
            </Group>
          </Box>
        </Collapse>

        <Group mt={10} mb={4} spacing={12}>
          <Text sx={{ fontSize: 13, color: tokens.muted }}>
            Showing {items.length} {items.length === 1 ? 'item' : 'items'}
          </Text>
          {stream.unactionedCount > 0 && (
            <Box
              sx={{
                padding: '4px 11px',
                borderRadius: 999,
                background: tokens.unreadTint,
                border: `1px solid ${tokens.pink}`,
                color: tokens.pink,
                fontSize: 12,
                fontWeight: 700,
                whiteSpace: 'nowrap',
              }}
            >
              {stream.unactionedCount} {stream.unactionedCount === 1 ? 'needs' : 'need'} action
            </Box>
          )}
          {/* LOS-6989: covers the unactioned notes in view — the search/source filters narrow
            it, and the label states N. */}
          {stream.unactionedCount > 0 && (
            <Box
              component="button"
              type="button"
              onClick={stream.markAllActioned}
              disabled={stream.isMarkingAll}
              sx={{
                marginLeft: 'auto',
                flex: 'none',
                whiteSpace: 'nowrap',
                border: 'none',
                background: 'none',
                padding: 0,
                color: tokens.pink,
                fontSize: 12,
                fontWeight: 600,
                cursor: stream.isMarkingAll ? 'default' : 'pointer',
                '&:hover:not(:disabled)': { color: tokens.blue },
                '&:disabled': { opacity: 0.6 },
              }}
            >
              {stream.isMarkingAll
                ? `Marking ${stream.unactionedCount} as actioned…`
                : `Mark all ${stream.unactionedCount} as actioned`}
            </Box>
          )}
        </Group>

        {stream.isLoading && items.length === 0 ? (
          <Group position="center" py="xl">
            <Loader size="sm" />
          </Group>
        ) : items.length === 0 ? (
          <Box py={48}>
            <Text align="center" sx={{ fontSize: 13, color: tokens.muted }}>
              {stream.totalCount === 0
                ? 'No activity on this trade yet.'
                : 'No activity matches the current filters.'}
            </Text>
          </Box>
        ) : (
          items.map((item, index) => {
            const isLast = index === items.length - 1
            switch (item.kind) {
              case 'note':
                return (
                  <NoteRow
                    key={item.id}
                    item={item}
                    isLast={isLast}
                    subTradeLabel={
                      stream.subTradeOptions.find(o => o.value === item.subTradeId)?.label
                    }
                    onMarkRead={stream.markRead}
                    onReopen={stream.reopen}
                    disabled={stream.isMarkingAll}
                  />
                )
              default:
                return null
            }
          })
        )}
      </Paper>
    </Stack>
  )
}
